import React, { useState, useEffect, useRef } from 'react';
import {
  Search,
  ChevronLeft,
  ChevronRight,
  Download,
  Eye,
  Mail,
  RefreshCw,
  UserCheck,
  Calendar,
  Shield,
  Ban,
  CheckCircle,
  XCircle,
  Users,
  MoreVertical,
  Edit,
  Trash2,
  Key
} from 'lucide-react';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import { toast } from 'react-toastify';
import api from '../services/api';

const RegisteredAlumni = () => {
  const [alumni, setAlumni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalAlumni, setTotalAlumni] = useState(0);
  const [selectedAlumni, setSelectedAlumni] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [departments, setDepartments] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0
  });

  useEffect(() => {
    fetchDepartments();
    fetchAlumni();
  }, [currentPage, itemsPerPage, searchTerm]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setOpenDropdownId(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const fetchDepartments = async () => {
    try {
      const response = await api.get('/departments');
      setDepartments(response.data);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchAlumni = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm
      };

      const response = await api.get('/admin/registered-alumni', { params });

      if (response.data.success) {
        setAlumni(response.data.alumni);
        setTotalAlumni(response.data.total);
        setStats({
          total: response.data.total,
          active: response.data.alumni.filter(a => a.is_active).length,
          inactive: response.data.alumni.filter(a => !a.is_active).length
        });
      }
    } catch (error) {
      console.error('Error fetching registered alumni:', error);
      toast.error('Failed to load registered alumni data');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    setOpenDropdownId(null);
  };

  const handleViewDetails = (alumnus) => {
    setSelectedAlumni(alumnus);
    setShowDetailsModal(true);
    setOpenDropdownId(null);
  };

  const handleEdit = (alumnus) => {
    setSelectedAlumni(alumnus);
    setShowEditModal(true);
    setOpenDropdownId(null);
  };

  const handleUpdateAlumni = async (e) => {
    e.preventDefault();
    try {
      const response = await api.put(`/admin/registered-alumni/${selectedAlumni.id}`, selectedAlumni);
      if (response.data.success) {
        toast.success('Alumni updated successfully');
        setShowEditModal(false);
        fetchAlumni();
      }
    } catch (error) {
      console.error('Error updating alumni:', error);
      toast.error('Failed to update alumni');
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      const response = await api.patch(`/admin/registered-alumni/${id}/toggle-status`);
      if (response.data.success) {
        toast.success(`Alumni ${currentStatus ? 'deactivated' : 'activated'} successfully`);
        fetchAlumni();
      }
    } catch (error) {
      console.error('Error toggling status:', error);
      toast.error('Failed to update status');
    }
    setOpenDropdownId(null);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this registered alumni? This action cannot be undone.')) {
      try {
        const response = await api.delete(`/admin/registered-alumni/${id}`);
        if (response.data.success) {
          toast.success('Alumni deleted successfully');
          fetchAlumni();
        }
      } catch (error) {
        console.error('Error deleting alumni:', error);
        toast.error('Failed to delete alumni');
      }
    }
    setOpenDropdownId(null);
  };

  const handleResetPassword = async (id) => {
    if (window.confirm('Reset password to admission number?')) {
      try {
        const response = await api.post(`/admin/registered-alumni/${id}/reset-password`);
        if (response.data.success) {
          toast.success('Password reset successfully');
        }
      } catch (error) {
        console.error('Error resetting password:', error);
        toast.error('Failed to reset password');
      }
    }
    setOpenDropdownId(null);
  };

  const handleExportCSV = async () => {
    try {
      const response = await api.get('/admin/registered-alumni/export', {
        params: { search: searchTerm },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `registered_alumni_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export successful!');
    } catch (error) {
      console.error('Error exporting alumni:', error);
      toast.error('Failed to export data');
    }
  };

  const openDropdown = (e, id) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX - 150
    });
    setOpenDropdownId(openDropdownId === id ? null : id);
  };

  const totalPages = Math.ceil(totalAlumni / itemsPerPage);

  // Get course display name with level

  const getGradeBadgeClass = (grade, examBody) => {
    if (grade === 'Mastery' || grade === 'Distinction') return 'bg-yellow-100 text-yellow-800';
    if (grade === 'Proficiency' || grade === 'Credit') return 'bg-blue-100 text-blue-800';
    if (grade === 'Competent' || grade === 'Pass') return 'bg-green-100 text-green-800';
    if (grade === 'First Class') return 'bg-purple-100 text-purple-800';
    return 'bg-gray-100 text-gray-800';
  };


const getCourseDisplay = (alumnus) => {
    if (!alumnus.course_name) return 'N/A';
    let display = alumnus.course_name;
    if (alumnus.exam_body_code === 'TVET-CDACC') {
      if (alumnus.level_number === 6) {
        display = display + ' (Level 6)';
      } else if (alumnus.level_number === 5) {
        display = display + ' (Level 5)';
      } else if (alumnus.level_number === 4) {
        display = display + ' (Level 4)';
      } else if (alumnus.level_number === 3) {
        display = display + ' (Level 3)';
      } else if (alumnus.level_number) {
        display = display + ' (Level ' + alumnus.level_number + ')';
      } else if (alumnus.level_name) {
        display = display + ' (' + alumnus.level_name + ')';
      }
    } else if (alumnus.exam_body_code === 'KNEC') {
      if (alumnus.level_name === 'Diploma') {
        display = display + ' (Diploma)';
      } else if (alumnus.level_name === 'Certificate') {
        display = display + ' (Certificate)';
      } else if (alumnus.level_name === 'Artisan') {
        display = display + ' (Artisan)';
      } else if (alumnus.level_name) {
        display = display + ' (' + alumnus.level_name + ')';
      }
    } else if (alumnus.exam_body_code === 'NITA') {
      if (alumnus.level_name === 'Grade I') {
        display = display + ' (Grade I)';
      } else if (alumnus.level_name === 'Grade II') {
        display = display + ' (Grade II)';
      } else if (alumnus.level_name === 'Grade III') {
        display = display + ' (Grade III)';
      } else if (alumnus.level_name) {
        display = display + ' (' + alumnus.level_name + ')';
      }
    } else if (alumnus.level_name) {
      display = display + ' (' + alumnus.level_name + ')';
    } else if (alumnus.level_number) {
      display = display + ' (Level ' + alumnus.level_number + ')';
    }
    return display;
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Registered Alumni</h1>
        <p className="text-gray-600 mt-1">Manage alumni who registered through the website (Regular users, no admin rights)</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Total Registered</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <div className="bg-blue-100 rounded-full p-3">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Active Accounts</p>
              <p className="text-2xl font-bold text-green-600">{stats.active}</p>
            </div>
            <div className="bg-green-100 rounded-full p-3">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Inactive Accounts</p>
              <p className="text-2xl font-bold text-red-600">{stats.inactive}</p>
            </div>
            <div className="bg-red-100 rounded-full p-3">
              <XCircle className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Search and Controls */}
      <div className="bg-white rounded-lg shadow mb-6 p-4">
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by name, admission number, email, or phone..."
              value={searchTerm}
              onChange={handleSearch}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <button
            onClick={fetchAlumni}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button
            onClick={handleExportCSV}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Alumni Table - Horizontal Scroll Container */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto" style={{ overflowX: 'auto', whiteSpace: 'nowrap' }}>
          <table className="min-w-[1200px] w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-12">#</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-40">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-32">Admission No.</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-32">Membership No.</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-48">Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-32">Phone</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-40">Department</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-48">Course (Level)</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-24">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-28">Registered</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-16">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="11" className="px-6 py-12 text-center">
                    <div className="flex justify-center items-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                      <span className="ml-2 text-gray-600">Loading registered alumni...</span>
                    </div>
                  </td>
                </tr>
              ) : alumni.length === 0 ? (
                <tr>
                  <td colSpan="11" className="px-6 py-12 text-center text-gray-500">
                    No registered alumni found
                  </td>
                </tr>
              ) : (
                alumni.map((alumnus, index) => (
                  <tr key={alumnus.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {(currentPage - 1) * itemsPerPage + index + 1}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center mr-2 flex-shrink-0">
                          <UserCheck className="w-4 h-4 text-primary" />
                        </div>
                        <div className="text-sm font-medium text-gray-900 truncate max-w-[150px]">{alumnus.full_name}</div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <code className="text-xs bg-gray-100 px-2 py-1 rounded">{alumnus.admin_number}</code>
                    </td>
                    <td className="px-4 py-3">
                      <code className="text-xs font-bold text-primary bg-primary/10 px-2 py-1 rounded">
                        {alumnus.alumni_membership_number || 'N/A'}
                      </code>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-900 truncate max-w-[180px]">{alumnus.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-900">{alumnus.telephone}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800 whitespace-nowrap">
                        {alumnus.department_name || 'N/A'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-700 truncate max-w-[200px]">
                        {getCourseDisplay(alumnus)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {alumnus.is_active ? (
                        <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 whitespace-nowrap">
                          ✓ Active
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800 whitespace-nowrap">
                          ✗ Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                      {new Date(alumnus.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="relative">
                        <button
                          onClick={(e) => openDropdown(e, alumnus.id)}
                          className="p-1 rounded-full hover:bg-gray-100 transition-colors"
                        >
                          <MoreVertical className="w-5 h-5 text-gray-500" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && totalAlumni > 0 && (
          <div className="px-6 py-4 bg-gray-50 border-t flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-700">Show:</span>
              <select
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
                className="border rounded-md px-2 py-1 text-sm"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-sm text-gray-700">
                Showing {((currentPage - 1) * itemsPerPage) + 1} to{' '}
                {Math.min(currentPage * itemsPerPage, totalAlumni)} of {totalAlumni}
              </span>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`px-3 py-1 border rounded-md transition-colors ${
                      currentPage === pageNum
                        ? 'bg-primary text-white border-primary'
                        : 'hover:bg-gray-100'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Floating Dropdown Menu - Positioned absolutely */}
      {openDropdownId && (
        <div 
          className="fixed bg-white rounded-lg shadow-lg border z-50 py-1 min-w-[160px]"
          style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
        >
          {alumni.find(a => a.id === openDropdownId) && (
            <>
              <button
                onClick={() => handleViewDetails(alumni.find(a => a.id === openDropdownId))}
                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
              >
                <Eye className="w-4 h-4 text-primary" />
                View Details
              </button>
              <button
                onClick={() => handleEdit(alumni.find(a => a.id === openDropdownId))}
                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
              >
                <Edit className="w-4 h-4 text-blue-600" />
                Edit
              </button>
              <button
                onClick={() => {
                  const alumnus = alumni.find(a => a.id === openDropdownId);
                  handleResetPassword(alumnus.id);
                }}
                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
              >
                <Key className="w-4 h-4 text-yellow-600" />
                Reset Password
              </button>
              <button
                onClick={() => {
                  const alumnus = alumni.find(a => a.id === openDropdownId);
                  handleToggleStatus(alumnus.id, alumnus.is_active);
                }}
                className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 ${
                  alumni.find(a => a.id === openDropdownId)?.is_active ? 'text-red-600' : 'text-green-600'
                }`}
              >
                {alumni.find(a => a.id === openDropdownId)?.is_active ? <Ban className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                {alumni.find(a => a.id === openDropdownId)?.is_active ? 'Deactivate' : 'Activate'}
              </button>
              <hr className="my-1" />
              <button
                onClick={() => {
                  const alumnus = alumni.find(a => a.id === openDropdownId);
                  handleDelete(alumnus.id);
                }}
                className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            </>
          )}
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedAlumni && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Registered Alumni Details</h2>
              <button onClick={() => setShowDetailsModal(false)} className="text-gray-500 hover:text-gray-700">
                ✕
              </button>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Full Name</label>
                  <p className="text-gray-900 font-medium mt-1">{selectedAlumni.full_name}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Membership Number</label>
                  <p className="text-primary font-bold mt-1">{selectedAlumni.alumni_membership_number || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Admission Number</label>
                  <p className="text-gray-900 font-mono mt-1">{selectedAlumni.admin_number}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Email Address</label>
                  <p className="text-gray-900 mt-1">{selectedAlumni.email}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Phone Number</label>
                  <p className="text-gray-900 mt-1">{selectedAlumni.telephone}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Department</label>
                  <p className="text-gray-900 mt-1">{selectedAlumni.department_name || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Course</label>
                  <p className="text-gray-900 mt-1">{getCourseDisplay(selectedAlumni)}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Status</label>
                  <p className="mt-1">
                    {selectedAlumni.is_active ? (
                      <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">Active</span>
                    ) : (
                      <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">Inactive</span>
                    )}
                  </p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Registration Date</label>
                  <p className="text-gray-900 mt-1">
                    {new Date(selectedAlumni.created_at).toLocaleString()}
                  </p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Last Login</label>
                  <p className="text-gray-900 mt-1">
                    {selectedAlumni.last_login ? new Date(selectedAlumni.last_login).toLocaleString() : 'Never'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedAlumni && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="border-b px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Edit Registered Alumni</h2>
              <button onClick={() => setShowEditModal(false)} className="text-gray-500 hover:text-gray-700">
                ✕
              </button>
            </div>
            <form onSubmit={handleUpdateAlumni} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                  <input
                    type="text"
                    value={selectedAlumni.full_name}
                    onChange={(e) => setSelectedAlumni({...selectedAlumni, full_name: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={selectedAlumni.email}
                    onChange={(e) => setSelectedAlumni({...selectedAlumni, email: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={selectedAlumni.telephone}
                    onChange={(e) => setSelectedAlumni({...selectedAlumni, telephone: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                  <select
                    value={selectedAlumni.department_id || ''}
                    onChange={(e) => setSelectedAlumni({...selectedAlumni, department_id: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">Select Department</option>
                    {departments.map(dept => (
                      <option key={dept.id} value={dept.id}>{dept.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button type="submit" className="flex-1 bg-primary text-white py-2 rounded-lg hover:bg-primary-dark">
                  Update
                </button>
                <button type="button" onClick={() => setShowEditModal(false)} className="flex-1 border border-gray-300 py-2 rounded-lg hover:bg-gray-50">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegisteredAlumni;
